import Joi from 'joi';
import moment from 'moment';
import React, { ChangeEvent, Component, KeyboardEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import UserDTO from '../../../../common/api/dtos/User';
import AppContext from '../../../../common/context/AppContext';
import { addCompensation, listCompensationDetails, updateCompensation } from '../../../../common/api/endpoints/compensations';
import { FormErrors } from '../../../../common/data/FormErrors';
import { processJoiError } from '../../../../common/helpers/processJoiError';
import { TRequestStatus } from '../../../../common/types/RequestStatus';
import ButtonControl from '../../../controls/ButtonControl/ButtonControl';
import ToggleControl from '../../../controls/ToggleControl/ToggleControl';
import ProgressBar, { IProgress, incrementProgress } from '../../../utils/ProgressBar/ProgressBar';
import RequestStatus from '../../../utils/RequestStatus/RequestStatus';
import SelectControl from '../../../controls/SelectControl/SelectControl';
import TextAreaControl from '../../../controls/TextAreaControl/TextAreaControl';
import { listUserContracts } from '../../../../common/api/endpoints/contracts';
import ContractDTO from '../../../../common/api/dtos/Contract';
import { listUsers } from '../../../../common/api/endpoints/users';
import { isEditPage } from '../../../../common/helpers/isEditPage';
import { loadComplete } from '../../../../common/helpers/LoadComplete';
import TextControl from '../../../controls/TextControl/TextControl';
import BreadcrumbControls from '../../../generics/Header/BreadcrumbControls';
import { withTransitionEvent } from '../../../TransitionEvent';
import { extractYears } from '../../../generics/Invoices/utils';

export interface Props extends RouteComponentProps<RouteParams> {
}

interface RouteParams {
  id?: string,
  eid?: string,
  entity?: string,
}

interface FormData {
  userId?: number,
  description: string,
  requestedOn: Date | null,
  honoredOn: Date | null,
  year: number,
  amount: number
  deleted: boolean,
  contractId?: number,
}

interface State {
  compensationId: number,
  progress: IProgress,
  pageStatus: TRequestStatus,
  formStatus: TRequestStatus,
  formData: FormData,
  formErrors: FormErrors,
  users?: UserDTO[],
  userContracts?: ContractDTO[],
}

type FormErrors = {
  [key in keyof FormData]?: string;
}

class CompensationDetail extends Component<Props, State> {
  formSchema = Joi.object({
    userId: Joi.number().required().messages(FormErrors.number),
    contractId: Joi.number().required().messages(FormErrors.number),
    description: Joi.string().trim(true).required().messages(FormErrors.string),
    requestedOn: Joi.date().required().messages(FormErrors.date),
    honoredOn: Joi.date().messages(FormErrors.date).allow(null, ''),
    year: Joi.number().required().messages(FormErrors.date),
    amount: Joi.number().required().messages(FormErrors.date),
    deleted: Joi.boolean(),
  })

  constructor(props: Props) {
    super(props);
    this.state = {
      compensationId: Number(this.props.match.params.id),
      formData: {
        description: '',
        requestedOn: null,
        honoredOn: null,
        year: (new Date()).getFullYear(),
        amount: 0,
        deleted: false,
        userId: this.props.match.params.entity == 'user' ? Number(this.props.match.params.eid) : undefined,
      },
      progress: {
        currentStep: 0,
        totalSteps: 0,
      },
      pageStatus: 'idle',
      formStatus: 'idle',
      formErrors: {},
    }
  }
  
  fetchCompensationDetails = async () => {
    this.setState({
      pageStatus: 'loading'
    });
    
    const compensation = await listCompensationDetails(this.state.compensationId!);
    this.setState(prevState => {
      return {
        formData: {
          description: compensation.description,
          requestedOn: compensation.requestedOn,
          honoredOn: compensation.honoredOn,
          deleted: compensation.deleted,
          year: compensation.year,
          amount: compensation.amount,
          userId: compensation.user?.id,
          contractId: compensation.contractId,
        },
        progress: incrementProgress(prevState.progress),
        pageStatus: 'success',
      }
    }, () => {
      this.fetchUserContracts();
    });
  }

  fetchUsers = async () => {
    this.setState({
      pageStatus: 'loading'
    });
    
    const users = await listUsers();

    this.setState(prevState => {
      return {
        pageStatus: 'success',
        users,
        progress: incrementProgress(prevState.progress),
      }
    });
  }

  fetchUserContracts = async () => {
    this.setState({
      pageStatus: 'loading'
    });
    
    const userContracts = await listUserContracts(this.state.formData.userId!);
    this.setState({
        pageStatus: 'success',
        userContracts: userContracts,
    });
  }

  addEntity = async () => {
    this.setState({
      formStatus: 'loading'
    });
    await addCompensation(this.state.formData);

    let entityName = this.props.match.params.entity;
    let entityId = this.props.match.params.eid;

    let returnPath = entityId ? `/${entityName}/edit/${entityId}/compensations` : '/compensation';

    this.setState({
        formStatus: 'success',
    }, () => {
      this.props.history.push({
        pathname: returnPath
      })
    });
  }

  updateEntity = async () => {
    this.setState({
      formStatus: 'loading'
    });
    await updateCompensation(this.state.formData, this.state.compensationId!);

    this.setState({
        formStatus: 'success',
    });
  }

  handleSubmit = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    const isValid = this.validateForm();

    if (isValid) {
      this.setState({
        formStatus: 'loading',
      });
  
      if(!isEditPage(Number(this.state.compensationId))) {
        this.addEntity();
      } else {
        this.updateEntity();
      }
    }
  }

  validateFormField = <K extends keyof FormData>(field: K) => {
    const subSchema = this.formSchema.extract(field);
    const result = subSchema.validate(this.state.formData[field], { abortEarly: false });

    if (result.error) {
      this.updateFormError(field, result.error.message);
    } else {
      this.updateFormError(field, "");
    }
  }

  validateForm = () => {
    // reset form errors
    this.setState({
      formErrors: {}
    });

    const result = this.formSchema.validate(this.state.formData, { abortEarly: false});
    if (result.error) {
      const formErrors = processJoiError(result.error);
      this.setState({
        // Assume type based on formSchema and Joi's error
        formErrors: formErrors as FormErrors,
      });

      return false;
    }

    return true;
  }

  componentDidMount() {
    this.fetchUsers();

    if(this.props.match.params.eid) {
      this.fetchUserContracts();
    }
    
    if(isEditPage(Number(this.state.compensationId))) {
      this.setState((prevState: State) => {
        return {
          progress: {
            currentStep: prevState.progress.currentStep,
            totalSteps: 1,
          }
        }
      });
      this.fetchCompensationDetails();
    }
  } 

  updateFormError<K extends keyof FormErrors>(field: K, value: FormErrors[K]) {
    this.setState(prevState => {
      return {
        formErrors: {
          ...prevState.formErrors,
          [field]: value,
        }
      }
    })
  }

  updateFormData<K extends keyof FormData>(field: K, value: FormData[K]) {
    const formData = this.state.formData;
    this.setState({
      formData: {
        ...formData,
        [field]: value,
      }
    })
  }

  renderBreadcrumbs() {
    const entity = this.props.match.params.entity;
    const entityId: number = Number(this.props.match.params.eid);

    let entityName;
    
    if(entity) {
      entityName = entity == 'user' ? this.state.users?.find((user: UserDTO) => user.id == entityId)!.name : 'Unknown Entity';
    }

    return(
      !entity ?
        <BreadcrumbControls
          items={[
            {label: 'compensations', path: '/compensations'},
            {label: `${!isEditPage(this.state.compensationId) ? 'adding' : 'editing'} compensation ${this.state.formData.description}`},
          ]}
          status={this.state.pageStatus}
        />
      :
        <BreadcrumbControls 
          items={[
            {label: `${entity}s`, path: `/${entity}`},
            {label: entityName ? entityName : '', path: `/${entity}/edit/${entityId}`},
            {label: 'compensations', path: `/${entity}/edit/${entityId}/compensations`},
            {label: `${!isEditPage(this.state.compensationId) ? 'adding' : 'editing'} compensation ${this.state.formData.description}`},
          ]}
          status={this.state.pageStatus}
        />
    )
  }

  renderForm() {
    const currentContract = this.state.userContracts?.find((userContract: ContractDTO) => userContract.id == this.state.formData.contractId);
    return(
      <div className="card">
        <SelectControl
          idName="userId"
          label="Signatory"
          required={true}
          value={this.state.formData.userId}
          options={this.state.users as UserDTO[] || []}
          disabled={!loadComplete(this.state.progress) || isEditPage(Number(this.state.compensationId)) || (!!this.props.match.params.eid && this.props.match.params.entity == 'user')}
          disabledValue={"Select a signatory"}
          onChange={this.setUserId}
          getValue={(op) => op?.id}
          getLabel={(op) => op?.fullName ? op.fullName : 'Unknown user'}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.userId}
        />
        <SelectControl
          idName="contractId"
          label="Contract"
          required={true}
          value={this.state.formData.contractId}
          onChange={this.setContractID}
          options={this.state.userContracts as ContractDTO[] || []}
          disabled={!loadComplete(this.state.progress) || isEditPage(Number(this.state.compensationId)) || !this.state.userContracts?.length}
          disabledValue={"Select a contract"}
          getValue={(op) => op?.id}
          getLabel={(op) => op?.name}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.userId}
        >
          {!isEditPage(Number(this.state.compensationId)) && <option value="" disabled selected>{this.state.userContracts?.length ? 'Select a contract' : 'No contracts found'}</option>}
        </SelectControl>
        {currentContract && 
          <div className="form-group">
            <div className="card">
              <div className="flex-row squeeze">
                <div className="column flex-v-center">
                  <span className="fas fa-exclamation-circle negative-text"></span>
                </div>
                <div className="column">
                  <small>{!isEditPage(Number(this.state.compensationId)) ? 'You are permanently attaching this compensation to the following contract:' : 'This compensation is permanently attached to the following contract:'}</small><br />
                  <small className="faint-text">
                    {currentContract.description}<br />
                    {moment(currentContract.startDate).format('LL')} - 
                    {currentContract.endDate ? moment(currentContract.endDate).format('LL') : ' indefinite'}<br />
                    {currentContract.tenant.legalName}<br />
                  </small>
                </div>
              </div>
            </div>
          </div>
        }
        <TextAreaControl
          label="Description"
          id="description"
          name="description"
          value={this.state.formData.description}
          onChange={this.setDescription}
          disabled={!loadComplete(this.state.progress)}
          required={true}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.description}
        />
        <TextControl
          label="Requested on"
          type="date"
          name="requestedOn"
          id="requestedOn"
          value={moment(this.state.formData.requestedOn).format('Y-MM-DD')}
          onChange={this.setRequestedOn}
          disabled={!loadComplete(this.state.progress)}
          br={true}
          required={true}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.requestedOn}
        />
        <TextControl
          label="Honored on"
          type="date"
          name="honoredOn"
          id="honoredOn"
          value={moment(this.state.formData.honoredOn).format('Y-MM-DD')}
          onChange={this.setHonoredOn}
          disabled={!loadComplete(this.state.progress)}
          br={true}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.honoredOn}
        />
        <SelectControl
          idName="year"
          label="For year"
          required={true}
          value={this.state.formData.year}
          options={extractYears(this.context.sysProps)}
          disabled={!loadComplete(this.state.progress)}
          onChange={this.setYear}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.year}
        />
        <TextControl
          label="Amount of days"
          type="number"
          name="amount"
          id="amount"
          value={this.state.formData.amount}
          onChange={this.setAmount}
          disabled={!loadComplete(this.state.progress)}
          br={true}
          required={true}
          onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          error={this.state.formErrors.amount}
        />  
        {isEditPage(Number(this.state.compensationId)) && 
          <ToggleControl
            id="deleted"
            name="deleted"
            changeMethod={this.setDeleted}
            isChecked={this.state.formData.deleted}
            isDisabled={!loadComplete(this.state.progress)}
            labelText="Deleted"
            onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
          />
        }
        <div className="form-group">
          <ButtonControl
            class="primary-button"
            onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              this.handleSubmit(ev)
            }}
            disabled={!loadComplete(this.state.progress)}
          >
            <RequestStatus status={this.state.formStatus} />
            <span className="text">Save</span>
          </ButtonControl>
        </div>
      </div>
    );
  }

  setUserId = (ev: React.MouseEvent<HTMLLIElement> | KeyboardEvent<Element>, userId: number | string | undefined | UserDTO) => {
    const formData = this.state.formData;
    this.setState({
      formData: {
        ...formData,
        userId: Number(userId),
      }
    }, () => {
      this.fetchUserContracts();
    })
  };

  setContractID = (ev: React.MouseEvent<HTMLLIElement> | KeyboardEvent<Element>, contractId: number | string | undefined | ContractDTO) => {
    const formData = this.state.formData;
    this.setState({
      formData: {
        ...formData,
        contractId: Number(contractId),
      }
    })
  };
  setDescription = (ev: ChangeEvent<HTMLTextAreaElement>) => this.updateFormData('description', ev.target.value);
  setYear = (ev: React.MouseEvent<HTMLLIElement, MouseEvent> | React.KeyboardEvent<Element>, yearNo: number | string | undefined) => this.updateFormData('year', Number(yearNo));
  setDeleted = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('deleted', ev.target.checked);
  setAmount = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('amount', Number(ev.target.value));
  setRequestedOn = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('requestedOn', ev.target.value ? new Date(ev.target.value) : null);
  setHonoredOn = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('honoredOn', ev.target.value ? new Date(ev.target.value) : null);

  render() {
    return (
     <React.Fragment>
      <ProgressBar
        currentStep={this.state.progress.currentStep}
        totalSteps={this.state.progress.totalSteps}
      />
      {this.renderBreadcrumbs()}
      <div className="flex-row">
        <div className="column">
          {this.renderForm()}
        </div>
      </div>
     </React.Fragment>
    );
  }
}

export default withTransitionEvent(CompensationDetail);

CompensationDetail.contextType = AppContext;